import { useEffect, useRef, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Router, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { LDProvider, useLDClient } from 'launchdarkly-react-client-sdk'
import ScrollMemory from 'react-router-scroll-memory'
import { QueryClientProvider } from 'react-query'

import history from '../history'

import { UpgradePanelProvider } from './navigation/UpgradePanelContext'

import Button from 'components/common/Button'
import { AlertText } from 'components/common/Alert'
import AuthenticatedRoutes from 'components/AuthenticatedRoutes'
import UnauthenticatedRoutes from 'components/UnauthenticatedRoutes'
import EmotionProvider from 'components/EmotionProvider'
import BusyOverlay from 'components/common/BusyOverlay'
import ToastContainer from 'components/common/ToastContainer'
import { MetadataProvider } from 'context/MetadataContext'
import { AppRefreshProvider } from 'context/AppRefreshContext'
import useAuth from 'hooks/use-auth'
import toast from 'lib/toast'
import { retryWithBackoff } from 'utils/retry'
import theme from 'theme'
import { gremlinQueryClient } from 'hooks/use-gremlin-query'
import { group, identify } from 'track'

function SegmentIdentity() {
  const {
    session,
    companyId,
    companyDisplayName,
    tier,
    currentTeam,
    currentTeamId,
    roles
  } = useAuth()
  // this is a ref so we don't re-render when it changes
  const areAnalyticsReady = useRef(0)
  // however, we do want this one to trigger a re-render, so it's in state
  // the "dependencies" here are:
  //     window.analytics being loaded correctly (the .VERSION property should be available)
  //     session.identifier is available
  const [areDependenciesReady, setAreDependenciesReady] = useState(false)

  // we need to retry our check to run the analytics call since we're waiting on a third party library to load
  // and we want to be sure that the session data is available before it loads
  useEffect(() => {
    if (areAnalyticsReady.current) {
      return
    }

    // wait for analytics library to be loaded and session available before we make the identify call
    // if we don't do this, then we'll create excessive Monthly Tracked Users and increase our Segment bill ($$$)
    if (!areDependenciesReady) {
      retryWithBackoff(
        50,
        10 * 1000,
        () => window.analytics?.VERSION && session.identifier,
        () => setAreDependenciesReady(true)
      )

      return
    }

    window.analytics.ready(() => {
      // Segment recommends not calling this for anon users
      identify(session.identifier, {
        email: session.identifier,
        firstName: session.first_name,
        lastName: session.last_name,
        companyId: companyId,
        companyName: companyDisplayName,
        companyRoles: session.roles,
        teamId: currentTeamId,
        teamName: currentTeam.name,
        roles: roles,
        tier: tier
      })

      group(companyId, {
        email: session.identifier,
        companyId: companyId,
        companyName: companyDisplayName,
        companyRoles: session.roles,
        teamId: currentTeamId,
        teamName: currentTeam.name,
        roles: roles,
        tier: tier,
        userId: session.identifier
      })
      // ensure this is only ever called once
      areAnalyticsReady.current = true
    })
  }, [
    areAnalyticsReady,
    areDependenciesReady,
    companyDisplayName,
    companyId,
    currentTeam.name,
    currentTeamId,
    roles,
    session.first_name,
    session.identifier,
    session.last_name,
    session.roles,
    tier
  ])

  return null
}

function useUserContext() {
  const { currentTeamId, currentTeam, companyId, tier, session } = useAuth()

  const userContext = useMemo(
    () => ({
      key: session.identifier,
      email: session.identifier,
      custom: {
        companyId: companyId,
        companyName: session.company_name,
        tier: tier,
        teamName: currentTeam.name,
        teamId: currentTeamId,
        role: currentTeam.role
      }
    }),
    [
      companyId,
      currentTeam.name,
      currentTeam.role,
      currentTeamId,
      session.company_name,
      session.identifier,
      tier
    ]
  )
  return userContext
}

function LaunchDarklyClientLoader({ children }) {
  const ldClient = useLDClient()
  const userContext = useUserContext()
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    async function identify(user) {
      try {
        await ldClient.identify(user)
      } catch (e) {
        // swallow ld error
      }
    }

    if (isReady) {
      identify(userContext)
    }
  }, [
    isReady,
    userContext,
    ldClient,
    userContext.custom.teamId,
    userContext.email
  ])

  useEffect(() => {
    ldClient &&
      ldClient
        .waitUntilReady()
        .then(() => {
          setIsReady(true)
          // Set on window like the default client would so we can use this in other areas
          window.ldClient = ldClient
        })
        .catch(() => {}) // swallow ld error
  }, [ldClient])

  return isReady ? children : <BusyOverlay />
}

LaunchDarklyClientLoader.propTypes = {
  children: PropTypes.node
}

function AdapterWrapper({ children }) {
  const userContext = useUserContext()

  return (
    <LDProvider
      clientSideID={process.env.REACT_APP_LAUNCHDARKLY_CLIENT_SIDE_ID}
      options={
        window.Cypress
          ? { streaming: false, sendEvents: false }
          : { bootstrap: 'localStorage' }
      }
      user={userContext}
    >
      <LaunchDarklyClientLoader>{children}</LaunchDarklyClientLoader>
    </LDProvider>
  )
}

AdapterWrapper.propTypes = {
  children: PropTypes.node
}

function CertificateExpirationContainer() {
  const timeoutRef = useRef()
  const { currentTeamId, currentTeam } = useAuth()

  useEffect(() => {
    if (currentTeam?.certificate_expiration_imminent ?? false) {
      timeoutRef.current = setTimeout(() => {
        toast({
          type: 'error',
          text: (
            <AlertText>
              The current certificate will expire within 90 days.&nbsp;
            </AlertText>
          ),
          action: (
            <Button
              secondary
              as={Link}
              to={`/settings/team/${currentTeamId}/configuration`}
            >
              Update your configuration
            </Button>
          )
        })
      }, 1000)
    }

    return () => clearTimeout(timeoutRef.current)
  }, [currentTeam, currentTeamId])

  return null
}

function App() {
  const { ready, session, contractExpirationImminent } = useAuth()
  const hasSession = Boolean(session.company_id)
  const expandHeader = contractExpirationImminent

  return (
    <Router history={history}>
      <ScrollMemory />
      <ToastContainer />
      <Helmet defaultTitle="Gremlin" titleTemplate="%s | Gremlin" />
      <EmotionProvider>
        {ready && (
          <div
            css={[
              {
                minWidth: theme.bp[0],
                minHeight: '100vh',
                display: 'grid'
              },

              expandHeader && {
                '--info-banner-height': '64px'
              }
            ]}
          >
            {hasSession ? (
              <AdapterWrapper>
                <SegmentIdentity />
                <CertificateExpirationContainer />
                <QueryClientProvider client={gremlinQueryClient}>
                  <MetadataProvider>
                    <AppRefreshProvider>
                      <UpgradePanelProvider>
                        <AuthenticatedRoutes />
                      </UpgradePanelProvider>
                    </AppRefreshProvider>
                  </MetadataProvider>
                </QueryClientProvider>
              </AdapterWrapper>
            ) : (
              <AppRefreshProvider>
                <UnauthenticatedRoutes />
              </AppRefreshProvider>
            )}
          </div>
        )}
        <BusyOverlay shown={!ready} />
      </EmotionProvider>
    </Router>
  )
}

App.propTypes = {
  authenticationStore: PropTypes.object
}

export default App
